import { computed, reactive, ref } from "vue";
import { fetchResource } from "#ce/utils/resource-fetcher";
import { watchImmediate } from "@vueuse/core";

/**
 * Loaded manifests keyed by their URL, shared by every sp-icon on the page.
 * An entry is `null` while its fetch is in flight.
 *
 * @type {Map<string, Object|null>}
 */
const manifests = reactive(new Map());

function loadManifest(iconsUrl) {
  if (!iconsUrl || manifests.has(iconsUrl)) {
    return;
  }

  manifests.set(iconsUrl, null);
  fetchManifest(iconsUrl);
}

// Icons are optional: a failed fetch (offline, CDN hiccup, missing file) leaves the
// icon unrendered rather than surfacing as an unhandled rejection — this runs
// fire-and-forget, so a throw has no caller to catch it.
async function fetchManifest(iconsUrl) {
  try {
    const response = await fetchResource(iconsUrl, { useCache: true });
    manifests.set(iconsUrl, await response.json());
  } catch {
    manifests.delete(iconsUrl); // a later mount can retry
  }
}

async function fetchSvg(url) {
  try {
    const response = await fetchResource(url, { useCache: true });
    return await response.text();
  } catch {
    return null;
  }
}

/**
 * Resolves a manifest entry to SVG markup. An entry is either inline SVG
 * source (full-sprite manifests) or an icon URL (index manifests), resolved
 * against the manifest's own URL so host-relative index entries still work
 * on third-party embed pages.
 *
 * @param {string} entry - The manifest entry.
 * @param {string} iconsUrl - The manifest URL the entry came from.
 * @returns {Promise<string|null>} The SVG markup.
 */
async function resolveManifestEntry(entry, iconsUrl) {
  if (entry.trimStart().startsWith("<")) {
    return entry;
  }

  return await fetchSvg(new URL(entry, new URL(iconsUrl, document.baseURI)).toString());
}

/**
 * Resolves the icon's SVG markup from, in order of precedence: the `svg`
 * prop, the manifest entry for `name` (via `iconsUrl`), or the `url` prop.
 * Manifests are fetched once per URL and shared across sp-icons.
 *
 * @param {import('vue').Ref<string>} name - The icon name (a ref or computed).
 * @param {import('vue').DefineProps<{
 *  iconsUrl: string;
 *  svg: string;
 *  url: string;
 * }>} props - The component props.
 * @returns {Object} The icon fetcher composable.
 * @property {import('vue').Ref<string|null>} svgNode - The resolved SVG markup.
 */
export function useIconFetcher(name, props) {
  const svgNode = ref(null);
  const manifestEntry = computed(() => manifests.get(props.iconsUrl)?.[name.value]);
  let resolveToken = 0;

  watchImmediate(() => props.iconsUrl, loadManifest);

  watchImmediate([manifestEntry, () => props.svg, () => props.url], resolveSvgNode);

  async function resolveSvgNode() {
    const token = ++resolveToken;
    const resolved = await resolveSource();

    // A stale resolution (the name or source changed mid-fetch) must not
    // clobber the newer one.
    if (token === resolveToken) {
      svgNode.value = resolved;
    }
  }

  async function resolveSource() {
    if (props.svg) {
      return props.svg;
    }

    if (manifestEntry.value) {
      return await resolveManifestEntry(manifestEntry.value, props.iconsUrl);
    }

    // A manifest-driven icon never falls back to `url`: a missing name stays
    // unrendered, matching the sprite behaviour.
    if (props.url && !(props.iconsUrl && name.value)) {
      return await fetchSvg(props.url);
    }

    return null;
  }

  return {
    svgNode,
  };
}
